import type { ComponentStyle } from '@/types/theme';

export type IconControlItem = {
  title: string;
  icon: string;
  clickHandler: (actionValue?: any) => void,
  substyle?: 'fas' | 'far';
  type?: ComponentStyle;
  disabled?: boolean
  color: string
};

export interface File {
  id: string;
  filename: string;
  bytes: number;
  mime_type: string | null;
  download_url: string;
  preview_url: string | null;
}

export interface UploadedFile {
  uid?: number,
  name?: string,
  file?: string,
  uploadId?: string,
  id?: string,
}

export interface Asset extends Blob {
  uid: number
  name: string
}

export interface ListType {
  name: string;
  value: string;
  disabled?: boolean;
}

export interface TranslateAction {
  language_code: string
  title?: string
  message: string
  is_default?: boolean
}

export interface TableMethods {
  clearCheck: () => void;
  setColumnFilters: (columnFilters: any) => void;
}

export interface SortParams {
  prop: string;
  order: string;
}

export interface Confirm {
  title?: string;
  msg?: string;
  type?: string;
  action?: string;
  actionName?: string;
  fields?: any//string[];
}

export interface FloodErrorResponse {
  payload: {
    seconds_to_wait_before_next_retry: number;
  },
  message: string;
}

export type Handle<T extends any[]> = (...args: T) => void;

export type EmitsToListeners<T> = {
  [K in keyof T]?:
  T[K] extends any[]
    ? (...args: T[K]) => void
    : T[K];
}

export interface ErrorList {
  message: string;
}

export type FormErrors<T = ErrorList[]> = { [type: string]: T }

export type ErrorMessage = string | ErrorList[];

// Проверка на any
type IsAny<T> = 0 extends (1 & T) ? true : false;

// Проверка на индексную сигнатуру (словарь с произвольными ключами)
type IsIndexSignature<T> = T extends object
  ? string extends keyof T
    ? true
    : false
  : false;

type IsTrimErrorTree<T, T2> = T2 extends null | undefined 
  ? T 
  : T | T2;

// Второй уровень конверсии запроса в ошибки.
type ToErrorMapValue2<T, T2> = T extends any[]
      ? T[number] extends object
        ? IsIndexSignature<T[number]> extends true
          ? IsTrimErrorTree<{ [key: string]: ErrorMessage }[], T2>
          : IsTrimErrorTree<ToErrorMap<T[number], T2>, T2>[]
        : ErrorMessage[]
      : T extends object
        ? IsIndexSignature<T> extends true
          ? IsTrimErrorTree<{ [key: string]: ErrorMessage }, T2>
          : IsTrimErrorTree<ToErrorMap<T, T2>, T2>
        : ErrorMessage;

// Первый уровень конверсии запроса в ошибки. Он был нужен чтобы избежать циклических зависимостей
type ToErrorMapValue<T, T2> = IsAny<T> extends true
  ? ErrorMessage
  : T extends null | undefined
    ? NonNullable<T>
    : ToErrorMapValue2<T, T2>

/** 
 * Класс-хелпер для коверсии типа запроса в тип ошибок
 * @param QueryType тип запроса
 * @param TrimType тип который может выставляться вместо узла дерева, если дерево ошибок внезапно прервётся
*/
export type ToErrorMap<QueryType, TrimType = undefined> = {
  [K in keyof QueryType]?: ToErrorMapValue<QueryType[K], TrimType>;
};

export interface ErrorResponse<T = FormErrors<ErrorList[]>> {
  status: number;
  data: {
    code: number;
    errors: T
    message: string;
  }
}

export function getErrorMessage(item?: ErrorMessage) {
  return item 
    ? typeof item === 'string'
      ? item
      : item[0]?.message || ''
    : '';
}

export function isErrorResponse(error: unknown): error is ErrorResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    typeof (error as ErrorResponse).data === 'object' &&
    true
  );
}
/**
 * Хелпер для более строгой проверки типов, будет выдавать ошибку если типы не пересекаются
 * 
 * @example 
 * ```typescript
 * type Foo = {
 *  foo: string;
 * }
 * 
 * type Bar = {
 *  bar: string;
 * }
 * 
 * type Baz = {
 *  baz: string;
 * } 
 * 
 * type Foobar = Foo | Bar;
 * 
 * let item: Foobar;
 * 
 * // пройдёт проверку
 * pickType<Foo>().from(item);
 * 
 * // выдаст ошибку
 * pickType<Baz>().from(item);
 * 
 * // так же можно использовать как более строгий typeGuard 
 * if(pickType<Foo>().typeGuard(item)) {
 *  // item is Foo
 * }
 * 
 * if(pickType<Baz>().typeGuard(item)) {
 *  // item is never
 * }
 * ```
 */
export function pickType<DerivedType>() {
  return {
    from: <BaseType>(
      item: Extract<BaseType, DerivedType> extends never 
        ? never
        : BaseType
      ) => {
      return item as Extract<DerivedType, BaseType>;
    },
    typeGuard: <BaseType>(item: BaseType): 
      item is DerivedType extends BaseType ? DerivedType : never => {
      return true;
    }
  };
}

export function typeGuard<T>(type: unknown, validate?: (error: any) => boolean): type is T {
  return validate ? validate(type) : true;
}

export type VuePropType<
    Component extends abstract new (...args: any) => any,
    PropName extends keyof InstanceType<Component>,
> = NonNullable<InstanceType<Component>[PropName]>;

export type SettingsPageQuery<T = any> = Omit<NonNullable<T>, 'offset' | 'filters'> & {
  page?: number;
  filters?: string;
};

export type DateRange = string[] | Date[];

export type FilterOp = '=' | '>' | '>=' | '<' | '<=' | '!=' | 'like' | 'notLike' | "between" | "notBetween";

export interface Filter {
  op: FilterOp | null;
  value: any;
}

export type FilterRecord = Record<string, Filter>;
export type TagSize = 'medium' | 'large';
